import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  MCP_APP_MIME_TYPE,
  MCP_APP_PROTOCOL_VERSION,
  clampAppHeight,
  safeExternalUrl,
  uiMessageText,
  withResourceCsp,
} from '../utils/mcpApps';

// MCP Apps（SEP-1865，協定 2026-01-26）宿主：把工具的 ui:// 資源放進沙箱 iframe 顯示，
// 並以 JSON-RPC 2.0 postMessage 回應畫面的請求（AGUI-1274，移植自 GenAIus McpAppFrame.tsx）。
// 支援：ui/initialize、tool-input／tool-result 通知、size-changed、host-context-changed（主題切換）、
// ui/message（以使用者身分送出）、ui/open-link、ping、ui/resource-teardown。
// tools/call、resources/read 目前不提供，一律回 -32601。

const MIN_HEIGHT = 160;
const MAX_HEIGHT = 1200;
const INITIAL_HEIGHT = 480;
// iframe 載入後這段時間內沒收到 ui/initialize 就視為失敗（顯示錯誤＋重試）
const INITIALIZE_TIMEOUT_MS = 15000;
const LOAD_ERROR_TEXT = '互動畫面無法載入';

// 主題判斷與 ChatMessage 相同：useTheme 在淺色時於 <html> 設 data-theme="light"，深色時移除該屬性
function currentTheme() {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement?.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
}

/**
 * 實際承載 iframe 的子元件。獨立成元件是為了 ui/resource-teardown：
 * React 移除一棵子樹時，會先執行子孫元件的 layout effect cleanup，之後才把 DOM 節點拔掉，
 * 因此在這裡的 useLayoutEffect cleanup 送出 teardown 時 iframe 仍在畫面上、訊息送得到
 * （若放在父層的 useEffect cleanup，iframe 早已被移除，postMessage 只會落空）。
 */
function McpAppIframe({ frameRef, html, title, ready, height }) {
  useLayoutEffect(() => {
    const frame = frameRef.current;
    return () => {
      // 畫面即將移除（轉為已完成、切換對話、重試重新載入）：通知一聲，不等回覆（best effort）
      try {
        frame?.contentWindow?.postMessage(
          { jsonrpc: '2.0', id: `teardown-${Date.now()}`, method: 'ui/resource-teardown', params: {} },
          '*'
        );
      } catch (e) {
        // ignore
      }
    };
  }, [frameRef]);

  return (
    <iframe
      ref={frameRef}
      title={title}
      srcDoc={html}
      // 只給 allow-scripts allow-forms，絕不可加 allow-same-origin：畫面跑在獨立的空白來源，
      // 碰不到 MyBro 頁面的 cookie、storage、DOM
      sandbox="allow-scripts allow-forms"
      className={`mcp-app-iframe${ready ? '' : ' is-hidden'}`}
      style={ready ? { height } : undefined}
    />
  );
}

/**
 * 單一工具結果的互動畫面。
 *
 * @param {object} props
 * @param {{resourceUri: string, server: string, toolName: string, toolInput?: object, toolResult?: object}} props.marker
 * @param {boolean} props.interactive 由父層決定：此畫面之後還沒有新的使用者訊息才可操作；
 *   false 時只顯示一行「<標題>（已完成）」，不載入 iframe
 * @param {string} props.title 顯示標題
 * @param {(marker: object) => Promise<{mimeType: string, html: string, meta: object}>} props.loadResource
 * @param {(text: string) => Promise<boolean>} props.onSendMessage 以使用者身分送出；resolve false 代表沒送出
 */
function McpAppFrame({ marker, interactive, title, loadResource, onSendMessage }) {
  const frameRef = useRef(null);
  const shellRef = useRef(null);
  const [state, setState] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [html, setHtml] = useState('');
  const [height, setHeight] = useState(INITIAL_HEIGHT);
  const [attempt, setAttempt] = useState(0);

  // 串流期間整個訊息列表會頻繁 re-render：最新 props 一律放 ref，
  // 讓訊息橋接只在「載入一份新文件」時掛一次，不因 re-render 反覆拆掛 listener
  const markerRef = useRef(marker);
  const interactiveRef = useRef(interactive);
  const loadResourceRef = useRef(loadResource);
  const onSendMessageRef = useRef(onSendMessage);
  markerRef.current = marker;
  interactiveRef.current = interactive;
  loadResourceRef.current = loadResource;
  onSendMessageRef.current = onSendMessage;

  const resourceKey = `${marker.server}|${marker.resourceUri}`;

  // 讀取畫面 HTML（只在可操作時讀；已完成的畫面不打 API）
  useEffect(() => {
    if (!interactive) return undefined;
    let cancelled = false;
    setState('loading');
    setHtml('');
    setHeight(INITIAL_HEIGHT);
    Promise.resolve()
      .then(() => loadResourceRef.current(markerRef.current))
      .then((resource) => {
        if (cancelled) return;
        if (resource?.mimeType && resource.mimeType !== MCP_APP_MIME_TYPE) {
          throw new Error(`互動畫面格式不正確（${resource.mimeType}）`);
        }
        if (!resource?.html) throw new Error('互動畫面沒有內容');
        setHtml(withResourceCsp(resource.html, resource.meta));
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[MCP App] 互動畫面載入失敗', { resourceKey, error: err?.message || err });
        setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [resourceKey, interactive, attempt]);

  const post = useCallback((message) => {
    frameRef.current?.contentWindow?.postMessage({ jsonrpc: '2.0', ...message }, '*');
  }, []);

  const hostContext = useCallback(() => ({
    theme: currentTheme(),
    platform: 'web',
    locale: 'zh-TW',
    timeZone: 'Asia/Taipei',
    displayMode: 'inline',
    availableDisplayModes: ['inline'],
    containerDimensions: { width: shellRef.current?.clientWidth || 720, maxHeight: MAX_HEIGHT },
    toolInfo: { tool: { name: markerRef.current.toolName } },
  }), []);

  // postMessage 橋接：每份載入的文件掛一次（iframe 以 key={attempt} 重建，與此 effect 同步更新）
  useEffect(() => {
    if (!html) return undefined;
    let initialized = false;
    const timeoutId = window.setTimeout(() => {
      if (initialized) return;
      console.warn('[MCP App] 互動畫面逾時未初始化');
      setState('error');
    }, INITIALIZE_TIMEOUT_MS);

    const respond = (id, result) => post({ id, result });
    const fail = (id, code, message) => {
      if (id !== undefined && id !== null) post({ id, error: { code, message } });
    };

    const onMessage = async (event) => {
      // 只處理自己這個 iframe 送來的訊息，其他來源一律忽略
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return;
      let message = event.data;
      if (typeof message === 'string') {
        try {
          message = JSON.parse(message);
        } catch (e) {
          return;
        }
      }
      if (!message || message.jsonrpc !== '2.0' || typeof message.method !== 'string') return;
      const { id, method, params } = message;
      try {
        switch (method) {
          case 'ui/initialize':
            initialized = true;
            window.clearTimeout(timeoutId);
            respond(id, {
              protocolVersion: (params && typeof params === 'object' && params.protocolVersion) || MCP_APP_PROTOCOL_VERSION,
              hostCapabilities: { message: {}, openLinks: {} },
              hostInfo: { name: 'MyBro', version: '1' },
              hostContext: hostContext(),
            });
            setState('ready');
            return;
          case 'ui/notifications/initialized': {
            const current = markerRef.current;
            post({ method: 'ui/notifications/tool-input', params: { arguments: current.toolInput || {} } });
            post({ method: 'ui/notifications/tool-result', params: current.toolResult || { content: [] } });
            return;
          }
          case 'ui/notifications/size-changed': {
            const next = clampAppHeight(params?.height, MIN_HEIGHT, MAX_HEIGHT);
            if (next !== null) setHeight(next);
            return;
          }
          case 'ui/message': {
            if (!interactiveRef.current) {
              fail(id, -32001, '這個互動畫面已完成，無法再送出');
              return;
            }
            const text = uiMessageText(params);
            if (!text) {
              fail(id, -32602, 'ui/message 只支援使用者文字訊息');
              return;
            }
            const sent = await onSendMessageRef.current?.(text);
            if (sent) respond(id, {});
            else fail(id, -32000, '訊息沒有送出');
            return;
          }
          case 'ui/open-link': {
            const url = safeExternalUrl(params?.url);
            if (!url) {
              fail(id, -32602, '只能開啟 http(s) 連結');
              return;
            }
            window.open(url, '_blank', 'noopener,noreferrer');
            respond(id, {});
            return;
          }
          case 'ping':
            if (id !== undefined && id !== null) respond(id, {});
            return;
          default:
            // 含 tools/call、resources/read：目前不提供
            fail(id, -32601, `Method not supported: ${method}`);
        }
      } catch (err) {
        fail(id, -32000, err?.message || '互動畫面請求失敗');
      }
    };

    window.addEventListener('message', onMessage);

    // 網頁切換深／淺色時同步通知畫面（useTheme 以 <html data-theme> 表示主題）
    const themeObserver = new MutationObserver(() => {
      if (initialized) post({ method: 'ui/notifications/host-context-changed', params: { hostContext: hostContext() } });
    });
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('message', onMessage);
      themeObserver.disconnect();
    };
    // attempt：重試時快取可能回傳同一份 HTML（html 字串不變），仍要重新計算初始化逾時
  }, [html, attempt, post, hostContext]);

  const label = title || '互動畫面';

  if (!interactive) {
    return <div className="mcp-app-completed">{label}（已完成）</div>;
  }

  return (
    // data-mcp-state：切換對話時 useChat 據此判斷互動畫面是否還在載入，等它就緒才顯示訊息區（AGUI-1302）
    <div ref={shellRef} className="mcp-app-frame" data-mcp-state={state}>
      {state === 'loading' && (
        <div role="status" className="mcp-app-status">正在載入{label}…</div>
      )}
      {state === 'error' && (
        <div role="alert" className="mcp-app-status mcp-app-error">
          <span>{LOAD_ERROR_TEXT}</span>
          <button
            type="button"
            className="mcp-app-retry"
            onClick={() => setAttempt((value) => value + 1)}
          >
            重試
          </button>
        </div>
      )}
      {html && state !== 'error' && (
        <McpAppIframe
          key={attempt}
          frameRef={frameRef}
          html={html}
          title={label}
          ready={state === 'ready'}
          height={height}
        />
      )}
    </div>
  );
}

export default McpAppFrame;
